import type { ComponentChildren } from "preact";
import { formatPrice } from "./formatters";
import { buildBundleCardCopy } from "./cancel-chat-cards";
import type { SizeUpBundle } from "./cancel-chat-bundle";

export interface BundleOfferProps {
  bundle: SizeUpBundle;
  currencySymbol?: string;
  /** The invitation line shown above the card; falls back to the built copy. */
  message?: string;
  /**
   * SizeUpReviewDialog only: summary totals cover the whole delivery period
   * (e.g. every 2 months → `/2 mo`) and the invite omits the trailing
   * "You'll save…" sentence. Cancel chat keeps monthly figures + full invite.
   */
  deliveryPeriodSummary?: boolean;
}

/** One label → value row of the savings box (left label, right figure). */
function Row({
  label,
  value,
  struck = false,
}: {
  label: string;
  value: string;
  /** The "before" figure — struck through and muted once there's a saving. */
  struck?: boolean;
}): ComponentChildren {
  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:text-sm tw:text-[#523D33]/80">
      <span>{label}</span>
      <span
        className={
          struck
            ? "tw:line-through tw:text-[#523D33]/50"
            : "tw:font-medium tw:text-[#523D33]"
        }
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Visual content for the "size-up bundle" retention offer: a bigger unit billed
 * less often, at a lower real cost per month. Deliberately mirrors the merge
 * dialog's savings box (MergeSubscriptionsDialog) to the letter — the same pink
 * bordered box, the same struck "before" figure, the same top-bordered bold
 * "You save" total. Buttons are NOT rendered here; the chat shows the reply
 * options separately.
 *
 * Every figure is deterministic (the model states none) and comes from the
 * pre-computed SizeUpBundle, so the card can't drift from the eligibility math.
 * When shipping was previewed for the target (and known for today) it is already
 * folded into the monthly figures — the invite line says so. See
 * cancel-chat-bundle.ts.
 *
 * Pass `deliveryPeriodSummary` from SizeUpReviewDialog to show whole-delivery
 * totals instead of per-month and drop the invite saving sentence.
 */
export function BundleOffer({
  bundle,
  currencySymbol = "$",
  message,
  deliveryPeriodSummary = false,
}: BundleOfferProps): ComponentChildren {
  const copy = buildBundleCardCopy(bundle, currencySymbol, {
    omitInviteSaving: deliveryPeriodSummary,
  });
  const months = bundle.newIntervalMonths;
  const formatAmount = (monthly: number) => {
    if (!deliveryPeriodSummary) {
      return `${currencySymbol}${formatPrice(monthly)}/mo`;
    }
    const total = Math.round(monthly * months * 100) / 100;
    const amount = `${currencySymbol}${formatPrice(total)}`;
    return months === 1 ? `${amount}/mo` : `${amount}/${months} mo`;
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <p className="tw:text-sm tw:m-0">{message?.trim() || copy.inviteLine}</p>

      <div className="tw:flex tw:flex-col tw:gap-1.5 tw:rounded-md tw:border tw:border-[#523D33]/15 tw:bg-[#FDEDEE]/50 tw:p-3">
        <Row
          label="You're paying now"
          value={formatAmount(bundle.currentMonthly)}
          struck
        />
        <Row label="You'll pay" value={formatAmount(bundle.newMonthly)} />
        {bundle.shippingIncluded ? (
          <p className="tw:m-0 tw:text-xs tw:text-[#523D33]/60">
            Prices include shipping
          </p>
        ) : null}
        <div className="tw:mt-1 tw:flex tw:items-center tw:justify-between tw:border-t tw:border-[#523D33]/10 tw:pt-2">
          <span className="tw:text-sm tw:font-bold tw:text-[#523D33]">
            You save
          </span>
          <span className="tw:text-base tw:font-extrabold tw:text-[#523D33]">
            {formatAmount(bundle.savingPerMonth)}
          </span>
        </div>
      </div>
    </div>
  );
}
